// src/config/entityRegistry.js

import * as department from '@/services/graphql/department'
import * as company from '@/services/graphql/company'
import * as organisation from '@/services/graphql/organisation'
import * as user from '@/services/graphql/user'
import * as entityConfig from '@/services/graphql/entityConfig'
import { readonly } from 'vue'

export const entityRegistry = {
  departments: {
    label: 'Department',

    summaryField: 'departmentsByFields',
    summarySelection: `
      id: _id
      name
      path
      isActive
      company {
        _id
        name
        path
        isActive
      }
      organisation {
        _id
        name
        path
        isActive
      }
    `,
    detailQuery: department.DETAIL_QUERY,
    createMutation: department.CREATE_MUTATION,
    updateMutation: department.UPDATE_MUTATION,
    deleteMutation: department.DELETE_MUTATION,
    groups: [
      {
        title: "Details", 
        fields: [
        { key: 'name', label: 'Name', type: 'text' ,requiredForCreation: true },
        { key: 'msSupervisorGroupID', label: 'Supervisor Group ID', readonly: (currentUser) => currentUser !== 'SysAdmin', type: 'text', readonly: () => true},
        { key: 'msUserGroupID', label: 'User Group ID', type: 'text', readonly: (currentUser) => currentUser !== 'SysAdmin'}, 
        { key: "path", label: "Path", type: "text"},
        { key: "archived", label: "Archived", component: "StringSwitch", readonly: (currentUser) => currentUser !== 'SysAdmin'},
        { key: "address", label: "Address", type: "object"},
        { key: "isActive", label: "Is Active", component: "StringSwitch"},
        { key: 'invoiceCode', label: 'Invoice Code', type: 'text', readonly: (currentUser) => currentUser !== 'SysAdmin'}, 
        { key: 'company', label: 'Company',component: "EntityField", readonly: (currentUser) => currentUser !== 'SysAdmin' && currentUser !== 'OrganisationAdmin', type: 'select', requiredForCreation: true, entityType: "companies", parentKey:"organisation", parentList: ["organisation", "company"], showIf: (model, currentUser) =>  currentUser === 'SysAdmin' || currentUser === 'OrganisationAdmin' ||  currentUser === 'CompanyAdmin' , listViewType: "department"},  
       ]
      }, 
    ]
  },

  companies: {
    label: 'Company',

    summaryField: 'companiesByFields',
    summarySelection: `
      id: _id
      name
      path
      isActive
      organisation {
        _id
        name
        path
        isActive
      }
    `,
    detailQuery: company.DETAIL_QUERY,
    createMutation: company.CREATE_MUTATION,
    updateMutation: company.UPDATE_MUTATION,
    deleteMutation: company.DELETE_MUTATION,
    groups: [
      {
        title: "Company Logo ", 
        fields: [
        { key: "logo", label: "Company Logo", component: "FileUpload", type: "image"},
        ]
      },
      {
        title: "Details", 
        fields: [
        { key: 'name', label: 'Name', type: 'text', requiredForCreation: true,requiredForCreation: true},
        { key: 'showOrgOnLogin', label: 'Show Organisation Name On Login?', component: "StringSwitch"},
        { key: "address", label: "Address", type: "object"},
        { key: "isActive", label: "Is Active", component: "StringSwitch"},
        { key: "primaryColour", label: "Primary Color", type: "text"},
        { key: "secondaryColour", label: "Secondary Color", type: "text"},
        { key: "welcomeEmail", label: "Welcome Email", type: "object"},
        { key: "resetEmail", label: "Reset Email", type: "object"},
        { key: "dataRetention", label: "Data Retention", type: "object"},
        { key: 'organisation', label: 'Organisation',component: "EntityField", readonly: (currentUser) => currentUser !== 'SysAdmin', parentList: ["organisation"],  requiredForCreation: true , type: 'select', entityType: "organisations" , showIf: (model, currentUser) => currentUser === 'SysAdmin' || currentUser === 'OrganisationAdmin', listViewType: "company" },  
       ]
      },
      {
        title: "Cost",
        fields:[
          { key: "cost", label: "Cost", type: "object", component: "CostsField", showIf: (model, currentUser) => currentUser === 'SysAdmin'}
        ]
      },
      {
        title: "Single Sign On (SSO) Settings",
        fields: [
          { key: 'msAllowSSO', label: 'Allow Microsoft Single Sign On (SSO)?', component: "StringSwitch", showIf: (model, currentUser) => currentUser === 'SysAdmin'}, 
          { key: "msTenantID", label: "Tenant ID", type: "text", showIf: (model, currentUser) => currentUser === 'SysAdmin'},
          { key: "msCompanyAdminGroupID", label: "Company Admin Group ID", type: "text", showIf: (model, currentUser) => currentUser === 'SysAdmin'}
        ]
      },
      {
        title: "Generate API Key", 
        fields: [
        { key: 'apiKey', label: 'Generate API Key', component: 'ButtonWithDisplay', action: "generateAPIKey", type: "button"}
    ]}
    ]
  },

  organisations: {
    label: 'Organisation',
    summaryField: 'organisationsByFields',
    summarySelection: `
      id: _id
      name
      path
      isActive
    `,
    detailQuery: organisation.DETAIL_QUERY,
    createMutation: organisation.CREATE_MUTATION,
    updateMutation: organisation.UPDATE_MUTATION,
    deleteMutation: organisation.DELETE_MUTATION,
    groups: [
      {
        title: "Organisation Logo ", 
        fields: [
        { key: "logo", label: "Organisation Logo", component: "FileUpload", type: "image"},
        ]
      },
      {
        title: "Details", 
        fields: [
        { key: 'name', label: 'Organisation Name', type: 'text' ,requiredForCreation: true},
        { key: 'archived', label: 'Is Archived', component: "StringSwitch", readonly: (currentUser) => currentUser !== 'SysAdmin', showIf: (model, currentUser) => currentUser === 'SysAdmin'},
        { key: "address", label: "Address", type: "object"},
        { key: "isActive", label: "Is Active", component: "StringSwitch", readonly: (currentUser) => currentUser !== 'SysAdmin', showIf: (model, currentUser) => currentUser === 'SysAdmin'},
        ]
      }, 
    ]
  },

  users: {
    label: 'User',
    summaryField: 'usersByFields',
    summarySelection: `
      username
      isActive
      department { path _id isActive }
      company {_id path isActive}
      organisation {_id path isActive}
      id: _id
    `,
    detailQuery: user.DETAIL_QUERY,
    createMutation: user.CREATE_MUTATION,
    updateMutation: user.UPDATE_MUTATION,
    deleteMutation: user.DELETE_MUTATION,
    groups: [
      {
        title: 'Set Password',
        fields: [
          { key: 'password', component: "PasswordField", label: "password" }
        ]
      },
      {
        title: "Details", 
        fields: [
        { key: 'username', label: 'Username', type: 'text', requiredForCreation: true, readonly: (currentUser, currentEntityState) => !!currentEntityState?.microsoft?.msSSOUniqueId},
        { key: 'email', label: 'Email', type: 'text',requiredForCreation: true, readonly: (currentUser, currentEntityState) => !!currentEntityState?.microsoft?.msSSOUniqueId},
        { key: 'name', type: "object" },
        { key: 'invoiceCode', label: 'Invoice Code', type: 'text' ,readonly: (currentUser) => currentUser !== 'SysAdmin'}, 
        { key: 'lastAccess', label: 'Last Access', type: "text", readonly: () => true }, 
        { key: 'accessLevel', label: 'AccessLevel', type: 'text', readonly: () => true, showIf: (model, currentUser) => currentUser === 'User' || currentUser === "Supervisor"}, 
        // { key: 'organisation', label: 'Organisation',component: "EntityField", type: 'select', entityType: "organisations", readonly: (currentUser) => currentUser !== 'SysAdmin', requiredForCreation: true}, 
        { key: 'organisation', label: 'Organisation',component: "EntityField", type: 'select', entityType: "organisations", readonly: (currentUser) => currentUser !== 'SysAdmin', requiredForCreation: true, listViewType: "user"}, 
        { key: 'company', label: 'Company',component: "EntityField", type: 'select', entityType: "companies", parentKey:"organisation", parentList: ["organisation"], readonly: (currentUser) => currentUser !== 'SysAdmin' && currentUser !== 'OrganisationAdmin', requiredForCreation: true,  listViewType: "user"}, 
        { key: 'department', label: 'Department',component: "EntityField", type: 'select', entityType: "departments", parentKey:"company" , parentList: ["organisation", "company"], readonly: (currentUser) => currentUser !== 'SysAdmin' && currentUser !== 'OrganisationAdmin' && currentUser !== 'CompanyAdmin', requiredForCreation: true, listViewType: "user"}, 
        { key: 'roles', label: 'Roles', component: "RolesField" , showIf: (model, currentUser) => currentUser === 'SysAdmin' || currentUser === 'OrganisationAdmin' || currentUser=='CompanyAdmin'}
      ]
      },
      {
        title: 'Reset MFA',
        fields: [
          { key: 'mfaReset', label: 'Reset MFA', component: 'ButtonWithDisplay', action: "resetMFAKey", type: "button", showIf: (model, currentUser) => currentUser === 'SysAdmin'}
        ]
      },
      {
        title: "Disable User",
        fields: [
          { key: 'isActive', label: 'Is Active', component: "StringSwitch", type:"boolean-disable", showIf: (model, currentUser) => currentUser === 'SysAdmin' || currentUser === 'OrganisationAdmin' || currentUser=='CompanyAdmin'},
        ]
      }
    ],
    validation:{
      "clarity":{
        "roles": ({department = null, company = null, organisation = null}) => ({
          "User":{
            priority: 4,
            entityPath: department?.path || company?.path || organisation?.path || "",
            entityId: department?._id || company?._id || organisation?._id || "",
            entityType: department && "department" || company && "company" || organisation && "organisation" || "",
            hasEntity: true,
          },
          "Supervisor":{
            priority: 1,
            entityPath: department?.path,
            entityId: department?._id,
            entityType: "department",
            hasEntity: !!department,
            errorMessage: "There is no corresponding department set for this user.",
          },
          "CompanyAdmin": { 
            priority: 2,
            entityPath:company?.path,
            entityId:company?._id,
            entityType: "company",
            hasEntity: !!company,
            errorMessage: "There is no corresponding company set for this user.",
          },
          "OrganisationAdmin":{
            priority: 3,
            entityPath: organisation?.path,
            entityId:organisation?._id,
            entityType: "organisation",
            hasEntity: !!organisation,
            errorMessage: "There is no corresponding organisation set for this user.",
          },
          "SysAdmin":{
            priority: 0,
            entityPath: "",
            entityId:"SysAdmin",
            entityType: "admin",
            hasEntity: true,
          }
        }) 
      }
    }
  },
  entityConfigs:{
    label: 'Entity Config',
    summaryField: entityConfig.SUMMARY_QUERY,
    detailQuery: entityConfig.DETAIL_QUERY,
    createMutation: entityConfig.CREATE_MUTATION,
    updateMutation: entityConfig.UPDATE_MUTATION,
    deleteMutation: entityConfig.DELETE_MUTATION,
  }
}
